// src/pages/Appointments/AppointmentDetails.jsx
// ✅ ENHANCED VERSION - Adds QuickActionsBar while preserving all existing features

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiArrowLeft,
  FiEdit,
  FiX,
  FiCalendar,
  FiClock,
  FiUser,
  FiMapPin,
  FiPhone,
  FiMail,
  FiShoppingBag,
  FiCheckCircle,
  FiAlertCircle,
  FiXCircle,
  FiMessageSquare,
  FiDollarSign,
  FiActivity,
  FiStar,
  FiCheck,
} from "react-icons/fi";
import {
  getAppointmentDetailsThunk,
  cancelAppointmentThunk,
  approveAppointmentThunk,
  rejectAppointmentThunk,
} from "../../store/thunks/appointmentThunks";
import Button from "../../components/common/Button";
import toast from "react-hot-toast";
import VisitTracking from "./Admin/VisitTracking";

// 🆕 NEW: Import QuickActionsBar component
import QuickActionsBar from "./components/QuickActionsBar";

const AppointmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedAppointment, loadingDetails } = useSelector(
    (state) => state.appointment
  );
  const [activeTab, setActiveTab] = useState("overview");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getAppointmentDetailsThunk(id));
    }
  }, [id, dispatch]);

  const handleRefresh = () => {
    dispatch(getAppointmentDetailsThunk(id));
  };

  const handleApprove = async () => {
    try {
      await dispatch(
        approveAppointmentThunk({
          appointmentId: id,
          approvalData: {},
        })
      ).unwrap();
      toast.success("Appointment approved successfully");
      handleRefresh();
    } catch (error) {
      toast.error(error || "Failed to approve appointment");
    }
  };

  const handleReject = async (reason, reasonDetails) => {
    try {
      await dispatch(
        rejectAppointmentThunk({
          appointmentId: id,
          rejectionData: { reason, reasonDetails },
        })
      ).unwrap();
      toast.success("Appointment rejected");
      setShowRejectModal(false);
      handleRefresh();
    } catch (error) {
      toast.error(error || "Failed to reject appointment");
    }
  };

  const handleCancel = async (reason, reasonDetails) => {
    try {
      await dispatch(
        cancelAppointmentThunk({
          appointmentId: id,
          cancellationData: { reason, reasonDetails },
        })
      ).unwrap();
      toast.success("Appointment cancelled successfully");
      setShowCancelModal(false);
      handleRefresh();
    } catch (error) {
      toast.error(error || "Failed to cancel appointment");
    }
  };

  if (loadingDetails || !selectedAppointment) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const appointment = selectedAppointment;
  const isPending = appointment.status === "PENDING";
  const canCancel =
    appointment.status === "PENDING" || appointment.status === "CONFIRMED";
  const canReschedule = appointment.status === "CONFIRMED";

  const showVisitTracking =
    appointment.status === "CONFIRMED" ||
    appointment.status === "CHECKED_IN" ||
    appointment.status === "IN_PROGRESS" ||
    appointment.status === "COMPLETED" ||
    appointment.status === "NO_SHOW";

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header - EXISTING */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-neutral-dark hover:text-primary mb-4 transition-colors"
        >
          <FiArrowLeft className="mr-2" />
          Back to Appointments
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-text">
                  {appointment.type?.replace(/_/g, " ")}
                </h1>
                <StatusBadge status={appointment.status} />
              </div>
              <p className="text-neutral-dark mb-1">
                {appointment.appointmentCode}
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-neutral-dark mt-4">
                <div className="flex items-center gap-2">
                  <FiCalendar className="text-primary" />
                  <span>
                    {new Date(appointment.scheduledDateTime).toLocaleDateString(
                      "en-IN",
                      {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FiClock className="text-primary" />
                  <span>
                    {new Date(appointment.scheduledDateTime).toLocaleTimeString(
                      "en-IN",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                    {" • "}
                    {appointment.duration} min
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FiMapPin className="text-primary" />
                  <span>{appointment.branchCode}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons - EXISTING */}
            <div className="flex flex-wrap gap-2">
              {/* Approve/Reject for Pending */}
              {isPending && (
                <>
                  <Button
                    variant="primary"
                    onClick={handleApprove}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <FiCheck className="mr-2" />
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowRejectModal(true)}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                  >
                    <FiXCircle className="mr-2" />
                    Reject
                  </Button>
                </>
              )}

              {/* Cancel for Pending/Confirmed */}
              {canCancel && (
                <Button
                  variant="outline"
                  onClick={() => setShowCancelModal(true)}
                  className="text-danger hover:text-danger-dark border-danger"
                >
                  <FiX className="mr-2" />
                  Cancel
                </Button>
              )}

              {/* Reschedule for Confirmed */}
              {canReschedule && (
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(`/appointments/${appointment._id}/reschedule`)
                  }
                >
                  <FiEdit className="mr-2" />
                  Reschedule
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 🆕 NEW: Quick Actions Bar */}
      {/* This provides quick access to admin assignment and field updates */}
      <QuickActionsBar appointment={appointment} onRefresh={handleRefresh} />

      {/* Visit Tracking Section - EXISTING */}
      {showVisitTracking && (
        <div className="mb-6">
          <VisitTracking appointment={appointment} onUpdate={handleRefresh} />
        </div>
      )}

      {/* Tabs - EXISTING */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <div className="flex gap-4 overflow-x-auto">
            {[
              { key: "overview", label: "Overview", icon: FiActivity },
              { key: "details", label: "Details", icon: FiMessageSquare },
              {
                key: "cart",
                label: "Cart Items",
                icon: FiShoppingBag,
                show: appointment.cartSnapshot?.items?.length > 0,
              },
              {
                key: "visit",
                label: "Visit Info",
                icon: FiUser,
                show: appointment.visit?.checkedIn,
              },
              {
                key: "timeline",
                label: "Timeline",
                icon: FiClock,
              },
            ]
              .filter((tab) => tab.show !== false)
              .map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap
                      ${
                        activeTab === tab.key
                          ? "border-primary text-primary font-medium"
                          : "border-transparent text-neutral-dark hover:text-primary"
                      }`}
                  >
                    <Icon size={18} />
                    {tab.label}
                  </button>
                );
              })}
          </div>
        </div>
      </div>

      {/* Tab Content - EXISTING */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === "overview" && <OverviewTab appointment={appointment} />}
        {activeTab === "details" && <DetailsTab appointment={appointment} />}
        {activeTab === "cart" && <CartTab appointment={appointment} />}
        {activeTab === "visit" && <VisitTab appointment={appointment} />}
        {activeTab === "timeline" && <TimelineTab appointment={appointment} />}
      </div>

      {/* Cancel Modal - EXISTING */}
      {showCancelModal && (
        <CancelModal
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancel}
        />
      )}

      {/* Reject Modal - EXISTING */}
      {showRejectModal && (
        <RejectModal
          onClose={() => setShowRejectModal(false)}
          onConfirm={handleReject}
          appointment={appointment}
        />
      )}
    </div>
  );
};

// ====================================
// ALL EXISTING COMPONENTS BELOW - UNCHANGED
// ====================================

// Status Badge Component
const StatusBadge = ({ status }) => {
  const getStatusStyle = () => {
    const styles = {
      PENDING: "bg-yellow-100 text-yellow-800 border-yellow-300",
      CONFIRMED: "bg-blue-100 text-blue-800 border-blue-300",
      CHECKED_IN: "bg-indigo-100 text-indigo-800 border-indigo-300",
      IN_PROGRESS: "bg-purple-100 text-purple-800 border-purple-300",
      COMPLETED: "bg-green-100 text-green-800 border-green-300",
      CANCELLED: "bg-red-100 text-red-800 border-red-300",
      NO_SHOW: "bg-gray-100 text-gray-800 border-gray-300",
      REJECTED: "bg-red-100 text-red-800 border-red-300",
    };
    return styles[status] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  return (
    <span
      className={`px-3 py-1 text-sm font-semibold rounded-full border ${getStatusStyle()}`}
    >
      {status?.replace(/_/g, " ")}
    </span>
  );
};

// Overview Tab
const OverviewTab = ({ appointment }) => (
  <div className="space-y-6">
    {/* Customer Information */}
    <div>
      <h3 className="text-lg font-semibold text-text mb-4 flex items-center gap-2">
        <FiUser className="text-primary" />
        Customer Information
      </h3>
      <div className="bg-neutral-lighter rounded-lg p-4 space-y-3">
        <InfoItem label="Name" value={appointment.userName} />
        <InfoItem label="Phone" value={appointment.userPhone} />
        {appointment.userEmail && (
          <InfoItem label="Email" value={appointment.userEmail} />
        )}
        {appointment.customerId && (
          <InfoItem label="Customer ID" value={appointment.customerId} />
        )}
      </div>
    </div>

    {/* Admin Information */}
    <div>
      <h3 className="text-lg font-semibold text-text mb-4 flex items-center gap-2">
        <FiUser className="text-primary" />
        Assigned Admin
      </h3>
      <div className="bg-neutral-lighter rounded-lg p-4 space-y-3">
        {appointment.adminName ? (
          <>
            <InfoItem label="Admin Name" value={appointment.adminName} />
            <InfoItem label="Admin Code" value={appointment.adminCode} />
          </>
        ) : (
          <p className="text-neutral-dark italic">No admin assigned yet</p>
        )}
      </div>
    </div>

    {/* Appointment Details */}
    <div>
      <h3 className="text-lg font-semibold text-text mb-4 flex items-center gap-2">
        <FiCalendar className="text-primary" />
        Appointment Details
      </h3>
      <div className="bg-neutral-lighter rounded-lg p-4 space-y-3">
        <InfoItem label="Type" value={appointment.type?.replace(/_/g, " ")} />
        <InfoItem label="Status" value={appointment.status} />
        <InfoItem label="Duration" value={`${appointment.duration} minutes`} />
        <InfoItem
          label="Booked By"
          value={appointment.booking?.bookedBy || "Customer"}
        />
        <InfoItem
          label="Booking Source"
          value={appointment.booking?.source || "Website"}
        />
        {appointment.booking?.bookedAt && (
          <InfoItem
            label="Booked On"
            value={new Date(appointment.booking.bookedAt).toLocaleString(
              "en-IN"
            )}
          />
        )}
      </div>
    </div>

    {/* Intent Information */}
    {appointment.intent && (
      <div>
        <h3 className="text-lg font-semibold text-text mb-4 flex items-center gap-2">
          <FiMessageSquare className="text-primary" />
          Customer Intent
        </h3>
        <div className="bg-neutral-lighter rounded-lg p-4 space-y-3">
          {appointment.intent.purpose && (
            <InfoItem
              label="Purpose"
              value={appointment.intent.purpose.replace(/_/g, " ")}
            />
          )}
          {appointment.intent.occasion && (
            <InfoItem
              label="Occasion"
              value={appointment.intent.occasion.replace(/_/g, " ")}
            />
          )}
          {appointment.intent.occasionDate && (
            <InfoItem
              label="Occasion Date"
              value={new Date(
                appointment.intent.occasionDate
              ).toLocaleDateString("en-IN")}
            />
          )}
          {appointment.intent.budget && (
            <InfoItem
              label="Budget"
              value={
                appointment.intent.budget.min && appointment.intent.budget.max
                  ? `₹${appointment.intent.budget.min.toLocaleString()} - ₹${appointment.intent.budget.max.toLocaleString()}`
                  : appointment.intent.budget.flexible
                  ? "Flexible"
                  : "Not specified"
              }
            />
          )}
        </div>
      </div>
    )}

    {/* Preferences */}
    {appointment.intent?.preferences && (
      <div>
        <h3 className="text-lg font-semibold text-text mb-4 flex items-center gap-2">
          <FiStar className="text-primary" />
          Preferences
        </h3>
        <div className="bg-neutral-lighter rounded-lg p-4 space-y-3">
          {appointment.intent.preferences.metals?.length > 0 && (
            <div>
              <span className="text-sm text-neutral-dark">Metals:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {appointment.intent.preferences.metals.map((metal) => (
                  <span
                    key={metal}
                    className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded"
                  >
                    {metal}
                  </span>
                ))}
              </div>
            </div>
          )}
          {appointment.intent.preferences.styles?.length > 0 && (
            <div>
              <span className="text-sm text-neutral-dark">Styles:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {appointment.intent.preferences.styles.map((style) => (
                  <span
                    key={style}
                    className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded"
                  >
                    {style}
                  </span>
                ))}
              </div>
            </div>
          )}
          {appointment.intent.preferences.categories?.length > 0 && (
            <div>
              <span className="text-sm text-neutral-dark">Categories:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {appointment.intent.preferences.categories.map((category) => (
                  <span
                    key={category}
                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                  >
                    {category}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Notes */}
    {appointment.notes && (
      <div>
        <h3 className="text-lg font-semibold text-text mb-4 flex items-center gap-2">
          <FiMessageSquare className="text-primary" />
          Notes
        </h3>
        <div className="bg-neutral-lighter rounded-lg p-4">
          <p className="text-sm text-text whitespace-pre-wrap">
            {appointment.notes}
          </p>
        </div>
      </div>
    )}
  </div>
);

// Details Tab
const DetailsTab = ({ appointment }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-semibold mb-4">Appointment Information</h3>
      <div className="space-y-2">
        <InfoItem
          label="Appointment Code"
          value={appointment.appointmentCode}
        />
        <InfoItem label="Branch" value={appointment.branchCode} />
        <InfoItem
          label="Scheduled Date"
          value={new Date(appointment.scheduledDateTime).toLocaleDateString(
            "en-IN",
            {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            }
          )}
        />
        <InfoItem
          label="Time"
          value={new Date(appointment.scheduledDateTime).toLocaleTimeString(
            "en-IN",
            {
              hour: "2-digit",
              minute: "2-digit",
            }
          )}
        />
        {appointment.endDateTime && (
          <InfoItem
            label="End Time"
            value={new Date(appointment.endDateTime).toLocaleTimeString(
              "en-IN",
              {
                hour: "2-digit",
                minute: "2-digit",
              }
            )}
          />
        )}
      </div>
    </div>

    {appointment.cancellation.cancelledAt && (
      <div className="border-l-4 border-red-500 bg-red-50 p-4">
        <h4 className="font-semibold text-red-800 mb-2">
          Cancellation Details
        </h4>
        <div className="space-y-1 text-sm">
          <p>
            <span className="font-medium">Reason:</span>{" "}
            {appointment.cancellation.reason?.replace(/_/g, " ")}
          </p>
          {appointment.cancellation.reasonDetails && (
            <p>
              <span className="font-medium">Details:</span>{" "}
              {appointment.cancellation.reasonDetails}
            </p>
          )}
          <p>
            <span className="font-medium">Cancelled By:</span>{" "}
            {appointment.cancellation.cancelledBy}
          </p>
          <p>
            <span className="font-medium">Cancelled At:</span>{" "}
            {new Date(appointment.cancellation.cancelledAt).toLocaleString(
              "en-IN"
            )}
          </p>
        </div>
      </div>
    )}

    {appointment.rejection && (
      <div className="border-l-4 border-red-500 bg-red-50 p-4">
        <h4 className="font-semibold text-red-800 mb-2">Rejection Details</h4>
        <div className="space-y-1 text-sm">
          <p>
            <span className="font-medium">Reason:</span>{" "}
            {appointment.rejection.reason?.replace(/_/g, " ")}
          </p>
          {appointment.rejection.reasonDetails && (
            <p>
              <span className="font-medium">Details:</span>{" "}
              {appointment.rejection.reasonDetails}
            </p>
          )}
          <p>
            <span className="font-medium">Rejected By:</span>{" "}
            {appointment.rejection.rejectedBy}
          </p>
          <p>
            <span className="font-medium">Rejected At:</span>{" "}
            {new Date(appointment.rejection.rejectedAt).toLocaleString("en-IN")}
          </p>
        </div>
      </div>
    )}
  </div>
);

// Cart Tab
const CartTab = ({ appointment }) => {
  if (!appointment.cartSnapshot?.items?.length) {
    return (
      <div className="text-center py-8">
        <FiShoppingBag className="mx-auto text-neutral-dark mb-2" size={48} />
        <p className="text-neutral-dark">No cart items</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Cart Snapshot</h3>
      <div className="space-y-3">
        {appointment.cartSnapshot.items.map((item, index) => (
          <div
            key={index}
            className="border border-neutral-light rounded-lg p-4 flex gap-4"
          >
            {item.images?.[0] && (
              <img
                src={item.images[0]}
                alt={item.name}
                className="w-20 h-20 object-cover rounded"
              />
            )}
            <div className="flex-1">
              <h4 className="font-medium text-text">{item.name}</h4>
              <p className="text-sm text-neutral-dark">{item.productCode}</p>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-sm">Qty: {item.quantity}</span>
                <span className="font-medium text-primary">
                  ₹{item.price?.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t pt-4 flex justify-between items-center">
        <span className="font-semibold">Total</span>
        <span className="text-xl font-bold text-primary">
          ₹{appointment.cartSnapshot.totalAmount?.toLocaleString("en-IN")}
        </span>
      </div>
    </div>
  );
};

// Visit Tab
const VisitTab = ({ appointment }) => {
  if (!appointment.visit?.checkedIn) {
    return (
      <div className="text-center py-8">
        <FiUser className="mx-auto text-neutral-dark mb-2" size={48} />
        <p className="text-neutral-dark">No visit information yet</p>
      </div>
    );
  }

  const visit = appointment.visit;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Visit Information</h3>
        <div className="space-y-2">
          <InfoItem
            label="Checked In"
            value={new Date(visit.checkInTime).toLocaleString("en-IN")}
          />
          {visit.checkOutTime && (
            <InfoItem
              label="Checked Out"
              value={new Date(visit.checkOutTime).toLocaleString("en-IN")}
            />
          )}
          {visit.actualDuration && (
            <InfoItem
              label="Duration"
              value={`${visit.actualDuration} minutes`}
            />
          )}
        </div>
      </div>

      {visit.productsViewed?.length > 0 && (
        <div>
          <h4 className="font-medium mb-3">Products Viewed</h4>
          <div className="space-y-2">
            {visit.productsViewed.map((product, index) => (
              <div key={index} className="bg-neutral-lighter p-3 rounded">
                <p className="font-medium">{product.productCode}</p>
                <p className="text-sm text-neutral-dark">
                  Viewed at: {new Date(product.viewedAt).toLocaleTimeString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {visit.productsTried?.length > 0 && (
        <div>
          <h4 className="font-medium mb-3">Products Tried</h4>
          <div className="space-y-2">
            {visit.productsTried.map((product, index) => (
              <div key={index} className="bg-neutral-lighter p-3 rounded">
                <p className="font-medium">{product.productCode}</p>
                <p className="text-sm text-neutral-dark">
                  Tried at: {new Date(product.triedAt).toLocaleTimeString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {visit.notes && (
        <div>
          <h4 className="font-medium mb-3">Visit Notes</h4>
          <div className="bg-neutral-lighter p-4 rounded">
            <p className="text-sm whitespace-pre-wrap">{visit.notes}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Timeline Tab
const TimelineTab = ({ appointment }) => {
  const events = [];

  // Booking event
  if (appointment.booking?.bookedAt) {
    events.push({
      time: appointment.booking.bookedAt,
      title: "Appointment Booked",
      details: `Booked by ${appointment.booking.bookedBy} via ${appointment.booking.source}`,
      icon: FiCalendar,
      color: "blue",
    });
  }

  // Approval/Rejection
  if (appointment.status === "CONFIRMED" && appointment.confirmedAt) {
    events.push({
      time: appointment.confirmedAt,
      title: "Appointment Approved",
      icon: FiCheckCircle,
      color: "green",
    });
  }

  if (appointment.status === "REJECTED" && appointment.rejection) {
    events.push({
      time: appointment.rejection.rejectedAt,
      title: "Appointment Rejected",
      details: `Reason: ${appointment.rejection.reason?.replace(/_/g, " ")}`,
      icon: FiXCircle,
      color: "red",
    });
  }

  // Visit events
  if (appointment.visit?.checkInTime) {
    events.push({
      time: appointment.visit.checkInTime,
      title: "Customer Checked In",
      icon: FiCheckCircle,
      color: "indigo",
    });
  }

  if (appointment.visit?.checkOutTime) {
    events.push({
      time: appointment.visit.checkOutTime,
      title: "Customer Checked Out",
      icon: FiCheckCircle,
      color: "green",
    });
  }

  // Cancellation
  if (appointment.cancellation.cancelledAt) {
    events.push({
      time: appointment.cancellation.cancelledAt,
      title: "Appointment Cancelled",
      details: `Reason: ${appointment.cancellation.reason?.replace(/_/g, " ")}`,
      icon: FiXCircle,
      color: "red",
    });
  }

  // Sort by time
  events.sort((a, b) => new Date(a.time) - new Date(b.time));

  if (events.length === 0) {
    return (
      <div className="text-center py-8">
        <FiClock className="mx-auto text-neutral-dark mb-2" size={48} />
        <p className="text-neutral-dark">No timeline events yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Activity Timeline</h3>
      <div className="space-y-4">
        {events.map((event, index) => {
          const Icon = event.icon;
          return (
            <div key={index} className="flex gap-4">
              {/* Icon */}
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-full bg-${event.color}-100 flex items-center justify-center`}
              >
                <Icon className={`text-${event.color}-600`} size={16} />
              </div>

              {/* Content */}
              <div className="flex-1 bg-neutral-light rounded-lg p-4">
                <h4 className="font-medium text-text mb-1">{event.title}</h4>
                <p className="text-sm text-neutral-dark">
                  {new Date(event.time).toLocaleString("en-IN", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </p>
                {event.details && (
                  <p className="text-sm text-neutral-dark mt-1">
                    {event.details}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Info Item Component
const InfoItem = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-neutral-light last:border-0">
    <span className="text-sm text-neutral-dark">{label}:</span>
    <span className="font-medium text-text">{value}</span>
  </div>
);

// Cancel Modal Component
const CancelModal = ({ onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [reasonDetails, setReasonDetails] = useState("");

  const reasons = [
    { value: "SCHEDULE_CONFLICT", label: "Schedule Conflict" },
    { value: "PERSONAL_REASON", label: "Personal Reason" },
    { value: "FOUND_ALTERNATIVE", label: "Found Alternative" },
    { value: "PRICE_CONCERN", label: "Price Concern" },
    { value: "LOCATION_ISSUE", label: "Location Issue" },
    { value: "OTHER", label: "Other" },
  ];

  const handleSubmit = () => {
    if (!reason) {
      toast.error("Please select a reason");
      return;
    }
    onConfirm(reason, reasonDetails);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold text-text mb-4">Cancel Appointment</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Cancellation *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select a reason</option>
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Details (Optional)
            </label>
            <textarea
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Provide more details..."
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Go Back
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-danger hover:bg-danger-dark"
          >
            Cancel Appointment
          </Button>
        </div>
      </div>
    </div>
  );
};

// Reject Modal Component
const RejectModal = ({ onClose, onConfirm, appointment }) => {
  const [reason, setReason] = useState("");
  const [reasonDetails, setReasonDetails] = useState("");

  const reasons = [
    { value: "UNAVAILABLE_TIME", label: "Time Slot Unavailable" },
    { value: "ADMIN_UNAVAILABLE", label: "Admin Unavailable" },
    { value: "BRANCH_CLOSED", label: "Branch Closed" },
    { value: "DUPLICATE_BOOKING", label: "Duplicate Booking" },
    { value: "INCOMPLETE_INFO", label: "Incomplete Information" },
    { value: "POLICY_VIOLATION", label: "Policy Violation" },
    { value: "OTHER", label: "Other" },
  ];

  const handleSubmit = () => {
    if (!reason) {
      toast.error("Please select a reason");
      return;
    }
    onConfirm(reason, reasonDetails);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold text-text mb-4">Reject Appointment</h3>

        {appointment && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm font-medium text-red-900 mb-1">
              {appointment.userName}
            </p>
            <p className="text-xs text-red-700">
              {new Date(appointment.scheduledDateTime).toLocaleString("en-IN")}
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Rejection *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select a reason</option>
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Details (Optional)
            </label>
            <textarea
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Explain why this appointment cannot be accommodated..."
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-red-600 hover:bg-red-700"
          >
            Reject Appointment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetails;
